import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { authFetch } from "@/lib/authFetch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { GitMerge, ChevronLeft, ChevronRight } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import type { Contact } from "@shared/schema";

type MergeLog = {
  id: string;
  contactKeptId: string;
  contactDeletedId: string;
  contactDeletedSnapshot: {
    prenom: string; nom: string; email: string | null; poste: string | null;
    organisationType: string | null; createdAt: string;
  };
  fieldsMerged: Record<string, "kept" | "taken_from_deleted"> | null;
  tachesRelinkedCount: number | null;
  detectionReason: string | null;
  similarityScore: string | null;
  mergedAt: string;
  keptContact?: Contact & { organisationNom?: string };
};

function avatarInitials(prenom: string, nom: string) {
  return ((prenom?.[0] || "") + (nom?.[0] || "")).toUpperCase() || "?";
}
function avatarColor(name: string) {
  const colors = ["bg-blue-500","bg-emerald-500","bg-violet-500","bg-orange-500","bg-pink-500","bg-teal-500"];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

const reasonLabel = (r: string | null) => ({
  auto_detected: "Détection auto",
  manual: "Fusion manuelle",
}[r || ""] || r || "—");

export default function MergeLogsPage() {
  const [page, setPage] = useState(1);
  const limit = 20;

  const { data, isLoading } = useQuery<{ logs: MergeLog[]; total: number }>({
    queryKey: ["/api/contacts/merge-logs", page],
    queryFn: async () => {
      const res = await authFetch(`/api/contacts/merge-logs?page=${page}&limit=${limit}`);
      if (!res.ok) throw new Error("Erreur chargement");
      return res.json();
    },
  });

  const logs = data?.logs || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {isLoading ? "Chargement…" : `${total} fusion${total !== 1 ? "s" : ""} au total`}
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-40">
          <p className="text-muted-foreground">Chargement…</p>
        </div>
      ) : logs.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-40 gap-2 text-muted-foreground border-2 border-dashed rounded-xl">
          <GitMerge className="h-8 w-8" />
          <p className="text-sm">Aucune fusion effectuée pour l'instant</p>
        </div>
      ) : (
        <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-secondary/40 border-b">
                <th className="text-left px-4 py-3 font-medium">Date</th>
                <th className="text-left px-4 py-3 font-medium">Fiche conservée</th>
                <th className="text-left px-4 py-3 font-medium hidden md:table-cell">Fiche supprimée</th>
                <th className="text-left px-4 py-3 font-medium hidden lg:table-cell">Raison</th>
                <th className="text-right px-4 py-3 font-medium hidden sm:table-cell">Tâches reroutées</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log, i) => (
                <tr key={log.id} className={`border-b hover:bg-muted/30 ${i % 2 === 0 ? "" : "bg-muted/10"}`}>
                  <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                    {log.mergedAt ? format(new Date(log.mergedAt), "d MMM yyyy HH:mm", { locale: fr }) : "—"}
                  </td>
                  <td className="px-4 py-3">
                    {log.keptContact ? (
                      <Link href={`/contacts/${log.contactKeptId}`} className="flex items-center gap-2 hover:text-primary">
                        <Avatar className="h-6 w-6 shrink-0">
                          <AvatarFallback className={`text-[9px] font-bold text-white ${avatarColor(log.keptContact.prenom + log.keptContact.nom)}`}>
                            {avatarInitials(log.keptContact.prenom, log.keptContact.nom)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium">{log.keptContact.prenom} {log.keptContact.nom}</span>
                      </Link>
                    ) : (
                      <span className="text-muted-foreground text-xs">ID: {log.contactKeptId.slice(0, 8)}…</span>
                    )}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Avatar className="h-6 w-6 shrink-0">
                        <AvatarFallback className={`text-[9px] font-bold text-white ${avatarColor(log.contactDeletedSnapshot.prenom + log.contactDeletedSnapshot.nom)}`}>
                          {avatarInitials(log.contactDeletedSnapshot.prenom, log.contactDeletedSnapshot.nom)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-xs">{log.contactDeletedSnapshot.prenom} {log.contactDeletedSnapshot.nom}</p>
                        {log.contactDeletedSnapshot.email && <p className="text-[10px] text-muted-foreground">{log.contactDeletedSnapshot.email}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <Badge variant="outline" className="text-[10px] h-4 px-1">
                      {reasonLabel(log.detectionReason)}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right hidden sm:table-cell">
                    <Badge variant="secondary" className="text-xs">
                      {log.tachesRelinkedCount ?? 0}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground">Page {page} / {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
